import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { renderFinalMarkdown } from '../../FileContentRender';
import { diff_match_patch } from 'diff-match-patch';
import { DiffEditor } from '@monaco-editor/react';
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  tablePlugin,
  codeBlockPlugin,
  codeMirrorPlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  CodeToggle,
  InsertTable,
  InsertThematicBreak,
  ListsToggle,
  diffSourcePlugin,
  linkPlugin,
  linkDialogPlugin,
  imagePlugin,
  frontmatterPlugin,
  useCodeBlockEditorContext
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import EnhancedMermaid from './EnhancedMermaid';


// 邮百工多主题系统
const themeStyles = `
  /* 1. 默认浅色主题 */
  .ybg-light-theme {
    --baseBase: #ffffff;
    --baseBgSubtle: #f8f9fa;
    --baseBg: #ffffff;
    --baseBgHover: #f1f3f4;
    --baseBgActive: #e8eaed;
    --baseLine: #dadce0;
    --baseBorder: #dadce0;
    --baseBorderHover: #bdc1c6;
    --baseSolid: #9aa0a6;
    --baseSolidHover: #80868b;
    --baseText: #3c4043;
    --baseTextContrast: #202124;

    --accentBase: #f8f9ff;
    --accentBgSubtle: #e8eaff;
    --accentBg: #d2e3fc;
    --accentBgHover: #aecbfa;
    --accentBgActive: #8ab4f8;
    --accentLine: #669df6;
    --accentBorder: #4285f4;
    --accentBorderHover: #1a73e8;
    --accentSolid: #1a73e8;
    --accentSolidHover: #1557b0;
    --accentText: #1a73e8;
    --accentTextContrast: #ffffff;

    --basePageBg: #ffffff;
    background: var(--basePageBg);
    color: var(--baseText);
  }

  /* 2. 邮百工深色主题 (当前使用) */
  .ybg-dark-theme {
    --baseBase: #252526;
    --baseBgSubtle: #2d2d30;
    --baseBg: #252526;
    --baseBgHover: #404040;
    --baseBgActive: #3c3c3c;
    --baseLine: #555;
    --baseBorder: #555;
    --baseBorderHover: #666;
    --baseSolid: #666;
    --baseSolidHover: #777;
    --baseText: #e6e6e6;
    --baseTextContrast: #f5f5f5;

    --accentBase: #252526;
    --accentBgSubtle: #2d2d30;
    --accentBg: #3c3c3c;
    --accentBgHover: #404040;
    --accentBgActive: #0078d4;
    --accentLine: #555;
    --accentBorder: #666;
    --accentBorderHover: #777;
    --accentSolid: #0078d4;
    --accentSolidHover: #106ebe;
    --accentText: #4fc3f7;
    --accentTextContrast: #ffffff;

    --basePageBg: rgba(37, 37, 38, 0.55);
    background: var(--basePageBg);
    color: var(--baseText);
  }

  /* 3. GitHub风格主题 */
  .ybg-github-theme {
    --baseBase: #ffffff;
    --baseBgSubtle: #f6f8fa;
    --baseBg: #ffffff;
    --baseBgHover: #f3f4f6;
    --baseBgActive: #e5e7ea;
    --baseLine: #d0d7de;
    --baseBorder: #d0d7de;
    --baseBorderHover: #8c959f;
    --baseSolid: #8c959f;
    --baseSolidHover: #656d76;
    --baseText: #24292f;
    --baseTextContrast: #1f2328;

    --accentBase: #f0f6ff;
    --accentBgSubtle: #dbeafe;
    --accentBg: #b6e3ff;
    --accentBgHover: #80ccff;
    --accentBgActive: #54aeff;
    --accentLine: #218bff;
    --accentBorder: #0969da;
    --accentBorderHover: #0550ae;
    --accentSolid: #0969da;
    --accentSolidHover: #0550ae;
    --accentText: #0969da;
    --accentTextContrast: #ffffff;

    --basePageBg: #ffffff;
    background: var(--basePageBg);
    color: var(--baseText);
  }

  /* 4. 紫色科技主题 */
  .ybg-purple-theme {
    --baseBase: #1a1523;
    --baseBgSubtle: #201b2e;
    --baseBg: #1a1523;
    --baseBgHover: #2a2438;
    --baseBgActive: #342d47;
    --baseLine: #463958;
    --baseBorder: #463958;
    --baseBorderHover: #58516a;
    --baseSolid: #6b627b;
    --baseSolidHover: #7e758c;
    --baseText: #e2ddee;
    --baseTextContrast: #f0ebf7;

    --accentBase: #1e1528;
    --accentBgSubtle: #291d3a;
    --accentBg: #3d2e5c;
    --accentBgHover: #523f7d;
    --accentBgActive: #6750a4;
    --accentLine: #7c61c7;
    --accentBorder: #9171ea;
    --accentBorderHover: #a682ff;
    --accentSolid: #9171ea;
    --accentSolidHover: #a682ff;
    --accentText: #b794ff;
    --accentTextContrast: #ffffff;

    --basePageBg: #1a1523;
    background: var(--basePageBg);
    color: var(--baseText);
  }

  /* 5. 绿色自然主题 */
  .ybg-green-theme {
    --baseBase: #fbfefb;
    --baseBgSubtle: #f6fdf6;
    --baseBg: #fbfefb;
    --baseBgHover: #f1fcf1;
    --baseBgActive: #e6f9e6;
    --baseLine: #d6f3d6;
    --baseBorder: #d6f3d6;
    --baseBorderHover: #b8e6b8;
    --baseSolid: #9dd99d;
    --baseSolidHover: #7acc7a;
    --baseText: #1b5e20;
    --baseTextContrast: #0d4d11;

    --accentBase: #f0fdf0;
    --accentBgSubtle: #dcfce7;
    --accentBg: #bbf7d0;
    --accentBgHover: #86efac;
    --accentBgActive: #4ade80;
    --accentLine: #22c55e;
    --accentBorder: #16a34a;
    --accentBorderHover: #15803d;
    --accentSolid: #16a34a;
    --accentSolidHover: #15803d;
    --accentText: #16a34a;
    --accentTextContrast: #ffffff;

    --basePageBg: #fbfefb;
    background: var(--basePageBg);
    color: var(--baseText);
  }

  /* 修复所有主题的内联代码背景问题 */
  .ybg-light-theme code,
  .ybg-dark-theme code,
  .ybg-github-theme code,
  .ybg-purple-theme code,
  .ybg-green-theme code {
    background-color: transparent !important;
    color: var(--baseText) !important;
    border: none !important;
    padding: 0 !important;
  }

  /* 代码块保留背景 */
  .ybg-light-theme pre code {
    background-color: #f6f8fa !important;
    color: #24292f !important;
    padding: 8px !important;
  }

  .ybg-dark-theme pre code,
  .ybg-purple-theme pre code {
    background-color: #1e1e1e !important;
    color: #d4d4d4 !important;
    padding: 8px !important;
  }

  .ybg-github-theme pre code {
    background-color: #f6f8fa !important;
    color: #24292f !important;
    padding: 8px !important;
  }

  .ybg-green-theme pre code {
    background-color: #f1fcf1 !important;
    color: #1b5e20 !important;
    padding: 8px !important;
  }

  /* 确保MDXEditor适应父容器滚动 - 适用于所有主题 */
  .ybg-light-theme .mdxeditor,
  .ybg-dark-theme .mdxeditor,
  .ybg-github-theme .mdxeditor,
  .ybg-purple-theme .mdxeditor,
  .ybg-green-theme .mdxeditor {
    overflow: visible !important;
    height: auto !important;
    max-height: none !important;
  }
  
  .ybg-light-theme .mdxeditor-rich-text-editor,
  .ybg-dark-theme .mdxeditor-rich-text-editor,
  .ybg-github-theme .mdxeditor-rich-text-editor,
  .ybg-purple-theme .mdxeditor-rich-text-editor,
  .ybg-green-theme .mdxeditor-rich-text-editor {
    overflow: visible !important;
    height: auto !important;
    max-height: none !important;
  }
  
  .ybg-light-theme .lexical-rich-text-editor,
  .ybg-dark-theme .lexical-rich-text-editor,
  .ybg-github-theme .lexical-rich-text-editor,
  .ybg-purple-theme .lexical-rich-text-editor,
  .ybg-green-theme .lexical-rich-text-editor {
    overflow: visible !important;
    height: auto !important;
    min-height: 300px !important;
    max-height: none !important;
  }

  /* 自定义Mermaid编辑器主题适配 */
  .ybg-light-theme .mermaid-code-editor textarea,
  .ybg-github-theme .mermaid-code-editor textarea,
  .ybg-green-theme .mermaid-code-editor textarea {
    background-color: #f6f8fa !important;
    color: #24292f !important;
    border: 1px solid #d0d7de !important;
  }

  .ybg-dark-theme .mermaid-code-editor textarea,
  .ybg-purple-theme .mermaid-code-editor textarea {
    background-color: #1e1e1e !important;
    color: #d4d4d4 !important;
    border: 1px solid #666 !important;
  }
`;

// 主题配置
const THEME_OPTIONS = [
  { value: 'light', label: '浅色主题', className: 'ybg-light-theme', icon: '☀️' },
  { value: 'dark', label: '深色主题', className: 'dark-theme dark-editor ybg-dark-theme', icon: '🌙' },
  { value: 'github', label: 'GitHub主题', className: 'ybg-github-theme', icon: '🐱' },
  { value: 'purple', label: '紫色科技', className: 'dark-theme dark-editor ybg-purple-theme', icon: '🔮' },
  { value: 'green', label: '绿色自然', className: 'ybg-green-theme', icon: '🌿' }
];

// 创建样式元素并插入到页面
const insertThemeStyles = () => {
  const existingStyle = document.getElementById('ybg-mdxeditor-theme');
  if (existingStyle) {
    existingStyle.remove();
  }
  
  const style = document.createElement('style');
  style.id = 'ybg-mdxeditor-theme';
  style.textContent = themeStyles;
  document.head.appendChild(style);
};

/**
 * 邮百工增强型MDXEditor组件
 * 集成MDXEditor + EnhancedMermaid + 编辑历史追踪
 */
const EnhancedMDXEditor = forwardRef(({
  content = '',                    // B变量：当前编辑内容
  originalContent = '',            // A变量：原始内容（永不改变）
  readOnly = true,                // 默认只读模式
  onContentChange = null,         // 内容变化回调
  enableHistory = true,           // 启用历史追踪
  enableLLMCommands = false,      // LLM命令支持
  onHistoryChange = null,         // 历史变化回调
  className = '',
  style = {},
  theme = 'dark', // 默认深色主题
  enableThemeSwitch = true, // 是否显示主题切换器
  fileId = null,                  // 添加文件ID追踪
  currentPhase = '',              // 🔑 新增：当前项目阶段
  documentPhase = '',             // 🔑 新增：文档所属阶段
  ws = null,                      // 🔑 新增：WebSocket连接
  addLocalMessage = null,         // 🔑 新增：本地消息添加函数
  ...otherProps
}, ref) => {
  const { t } = useTranslation();
  // 状态管理
  const [currentContent, setCurrentContent] = useState(content);
  const [editorKey, setEditorKey] = useState(0); // 用于强制重新渲染MDXEditor
  const [lastExternalContent, setLastExternalContent] = useState(content); // 记录上次外部传入的content
  const [isEditing, setIsEditing] = useState(!readOnly);
  const [diffMode, setDiffMode] = useState(false);
  const [comparisonContent, setComparisonContent] = useState('');

  // MDXEditor引用
  const mdxEditorRef = useRef(null);
  const editorContainerRef = useRef(null);



  const [currentTheme, setCurrentTheme] = useState(theme); // 当前主题
  const mermaidTheme = (currentTheme === 'light' || currentTheme === 'github') ? 'default' : 'dark';
  
  // 检测内容是否包含数学公式（$ 或 $$）
  // MDXEditor 不支持数学公式渲染，需要使用 preview 模式
  const containsMathFormula = (text) => {
    if (!text) return false;
    // 检测 $...$ 或 $$...$$ 格式的数学公式
    // 排除 $0 这样的货币金额（美元符号后紧跟数字）
    return /\$\$[\s\S]+?\$\$/.test(text) || /\$[^$\s][^$]*[^$\s]\$/.test(text) || /\$[^$\s]\$/.test(text);
  };
  
  // 新增：渲染模式状态 - 如果内容包含数学公式，默认使用 preview 模式
  const getInitialRenderMode = () => {
    if (containsMathFormula(content)) {
      console.log('MDXEditor: 检测到数学公式，使用 preview 模式');
      return 'preview';
    }
    return 'mdx';
  };
  
  const [renderMode, setRenderMode] = useState(getInitialRenderMode); // 'mdx' | 'preview' | 'source'
  const [mdxError, setMdxError] = useState(null);
  const [sourceContent, setSourceContent] = useState(content);
  
  // 修订显示相关状态
  const [revisionMode, setRevisionMode] = useState(false);
  const [diffResults, setDiffResults] = useState(null);

  // 获取当前主题配置
  const currentThemeConfig = THEME_OPTIONS.find(t => t.value === currentTheme) || THEME_OPTIONS[1];

  // 主题切换函数
  const handleThemeChange = (newTheme) => {
    console.log('MDXEditor: 切换主题', { from: currentTheme, to: newTheme });
    setCurrentTheme(newTheme);
    // 主题变化时，MDXEditor会自动重新渲染
  };

  // 新增：MDX错误处理函数
  const handleMDXError = (error) => {
    console.error('MDXEditor渲染失败:', error);
    setMdxError({
      message: error?.message || error || '渲染错误',
      timestamp: new Date().toLocaleTimeString(),
      contentLength: currentContent?.length || 0
    });
    
    // 延迟切换到预览模式，避免编辑过程中频繁切换
    setTimeout(() => {
      setRenderMode('preview');
    }, 1000); // 1秒延迟
  };

  // 新增：模式切换函数
  const handleModeSwitch = (newMode) => {
    console.log('MDXEditor: 切换渲染模式', { from: renderMode, to: newMode });
    
    // 如果从 MDX 模式切换，尝试获取 MDXEditor 的最新内容
    if (renderMode === 'mdx' && mdxEditorRef.current) {
      try {
        const latestContent = mdxEditorRef.current.getMarkdown();
        if (latestContent && latestContent !== currentContent) {
          console.log('从 MDXEditor 获取最新内容:', { 
            oldLength: currentContent.length, 
            newLength: latestContent.length 
          });
          setCurrentContent(latestContent);
          // 立即同步到父组件
          if (onContentChange) {
            onContentChange(fileId, latestContent, 'mdx-sync-on-switch');
          }
        }
      } catch (error) {
        console.warn('无法从 MDXEditor 获取内容:', error);
      }
    }
    
    // 模式切换前立即同步当前内容，确保修订计算基于最新内容
    if (window.mdxContentSyncTimeout) {
      clearTimeout(window.mdxContentSyncTimeout);
      // 立即执行待同步的内容保存
      if (onContentChange && currentContent) {
        onContentChange(fileId, currentContent, 'mode-switch-sync');
        console.log('模式切换前同步内容:', { 
          length: currentContent.length, 
          preview: currentContent.substring(0, 100) + '...' 
        });
      }
    }
    
    setRenderMode(newMode);
    
    // 清除错误状态（如果切换到MDX模式）
    if (newMode === 'mdx') {
      setMdxError(null);
      setEditorKey(prev => prev + 1); // 强制重新渲染
    }
  };

  // 修订显示切换函数
  const handleRevisionToggle = () => {
    const newRevisionMode = !revisionMode;
    setRevisionMode(newRevisionMode);
    console.log('修订显示模式:', newRevisionMode ? '开启' : '关闭');
  };

  // 计算文本差异
  const calculateDiff = (original, current) => {
    const dmp = new diff_match_patch();
    const diffs = dmp.diff_main(original, current);
    dmp.diff_cleanupSemantic(diffs);
    
    let insertCount = 0;
    let deleteCount = 0;
    
    diffs.forEach(([operation, text]) => {
      if (operation === 1) insertCount += text.length; // 插入
      if (operation === -1) deleteCount += text.length; // 删除
    });
    
    return {
      diffs,
      insertCount,
      deleteCount
    };
  };


  
  // 双层状态管理：本地状态 + 防抖同步到父组件
  const handleContentChange = (newContent) => {
    // 立即更新本地状态（保证输入流畅）
    setCurrentContent(newContent);
    
    // 防抖通知父组件（避免频繁重新渲染）
    if (onContentChange) {
      clearTimeout(window.mdxContentSyncTimeout);
      
      // 注册全局立即保存函数，包含文件ID信息
      window.saveCurrentFileContent = () => {
        onContentChange(fileId, newContent, 'force-save');
      };
      
      window.mdxContentSyncTimeout = setTimeout(() => {
        onContentChange(fileId, newContent, 'manual');
        // 清除全局保存函数
        window.saveCurrentFileContent = null;
      }, 1000); // 1秒防抖，用户停止输入后才同步
    }
  };

  // 实时计算修订差异 - 始终计算
  useEffect(() => {
    if (originalContent && currentContent) {
      const diffResult = calculateDiff(originalContent, currentContent);
      setDiffResults(diffResult);
      
      // 调试信息：跟踪修订计算

    } else {
      setDiffResults(null);
      console.log('修订差异重置:', { originalContent: !!originalContent, currentContent: !!currentContent });
    }
  }, [originalContent, currentContent, renderMode]);

  // 监听MDXEditor实例的变化
  const mdxEditorReadyRef = useRef(false);
  
  useEffect(() => {
    // 如果MDXEditor实例存在且内容不同步，则更新内容
    if (mdxEditorRef.current && mdxEditorReadyRef.current && renderMode === 'mdx') {
      const currentEditorContent = mdxEditorRef.current.getMarkdown() || '';
      if (currentEditorContent !== currentContent && currentContent) {
        //console.log('检测到MDXEditor内容与当前内容不同步，正在更新...');
        mdxEditorRef.current.setMarkdown(currentContent);
      }
    }
  }, [currentContent, renderMode, mdxEditorRef.current]);

  // 暴露给外部的API方法
  useImperativeHandle(ref, () => ({
    // 基础内容操作 - 直接使用MDXEditor的原生方法
    setContent: (newContent) => {
      console.log('MDXEditor API: setContent called', { newContent: newContent?.substring(0, 50) + '...' });
      if (mdxEditorRef.current?.setMarkdown) {
        mdxEditorRef.current.setMarkdown(newContent || '');
        mdxEditorReadyRef.current = true;
      }
    },

    getContent: () => {
      return mdxEditorRef.current?.getMarkdown() || currentContent;
    },

    insertContent: (content) => {
      if (mdxEditorRef.current?.insertMarkdown) {
        mdxEditorRef.current.insertMarkdown(content);
        mdxEditorReadyRef.current = true;
      }
    },

    // 模式切换
    setReadOnly: (readonly) => {
      setIsEditing(!readonly);
    },

    // 差异对比
    showDiff: (compareContent) => {
      setComparisonContent(compareContent);
      setDiffMode(true);
    },

    hideDiff: () => {
      setDiffMode(false);
      setComparisonContent('');
    }
  }));

  // 自定义代码块渲染器
  const customCodeBlockRenderers = {
    mermaid: ({ code }) => (
      <div style={{ margin: '16px 0' }}>
        <EnhancedMermaid 
          chart={code} 
          theme={mermaidTheme}
          zoomEnabled={true}
          showDebugInfo={false}
          onChartUpdate={(updatedCode) => {
            // 如果图表被更新（比如通过AI修复），可以在这里处理
            console.log('Mermaid图表已更新:', updatedCode);
          }}
        />
      </div>
    )
  };

  // 配置MDXEditor插件
  const plugins = [
    // 基础插件
    headingsPlugin(),
    listsPlugin(),
    quotePlugin(),
    thematicBreakPlugin(),
    markdownShortcutPlugin(),
    
    // 表格支持
    tablePlugin(),
    
    // 代码块支持（包含自定义Mermaid渲染器）
    codeBlockPlugin({
      defaultCodeBlockLanguage: 'txt',
      codeBlockEditorDescriptors: [
        {
          match: (language) => language === 'mermaid',
          priority: 1,
          Editor: (props) => {
            const [showCodeEditor, setShowCodeEditor] = useState(false);
            const [isHovering, setIsHovering] = useState(false);
            
            // 使用MDXEditor提供的正确上下文
            const cb = useCodeBlockEditorContext();
            
            // 创建textarea的ref用于更好的控制
            const textareaRef = useRef(null);
            const updateTimeoutRef = useRef(null);
            
            // 防抖更新函数
            const debouncedUpdate = (value) => {
              if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
              }
              updateTimeoutRef.current = setTimeout(() => {
                cb.setCode(value);
              }, 150); // 150ms防抖
            };
            
            // 清理定时器
            useEffect(() => {
              return () => {
                if (updateTimeoutRef.current) {
                  clearTimeout(updateTimeoutRef.current);
                }
              };
            }, []);

            // 键盘快捷键支持
            useEffect(() => {
              const handleKeyDown = (e) => {
                if (e.key === 'Escape' && showCodeEditor) {
                  setShowCodeEditor(false);
                }
              };

              if (showCodeEditor) {
                document.addEventListener('keydown', handleKeyDown);
                return () => document.removeEventListener('keydown', handleKeyDown);
              }
            }, [showCodeEditor]);

            return (
              <div className="mermaid-code-editor" style={{ 
                border: '1px solid #555', 
                borderRadius: '4px', 
                overflow: 'hidden', 
                backgroundColor: '#2d2d30',
                position: 'relative'
              }}>
                {!showCodeEditor ? (
                  // 只显示图表模式
                  <div 
                    style={{ 
                      position: 'relative',
                      minHeight: '300px',
                      padding: '12px'
                    }}
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                  >
                    {/* 悬浮编辑按钮 */}
                    {isHovering && (
                      <button
                        onClick={() => setShowCodeEditor(true)}
                        style={{
                          position: 'absolute',
                          top: '16px',
                          left: '16px',
                          zIndex: 10,
                          padding: '6px 10px',
                          fontSize: '12px',
                          background: 'rgba(0, 120, 212, 0.9)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          transition: 'all 0.2s ease'
                        }}
                        title="点击编辑Mermaid代码"
                      >
                        ✏️ 编辑图代码重绘
                      </button>
                    )}

                    {/* 图表显示 */}
                    <EnhancedMermaid 
                      chart={props.code || 'graph TD\n  A[开始] --> B[结束]'} 
                      theme={mermaidTheme}
                      zoomEnabled={true}
                      showDebugInfo={false}
                    />
                  </div>
                ) : (
                  // 显示图表+代码编辑器模式
                  <div>
                    {/* 顶部工具栏 */}
                    <div style={{ 
                      background: '#3c3c3c', 
                      padding: '8px 12px', 
                      borderBottom: '1px solid #555',
                      fontSize: '12px',
                      color: '#cccccc',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span>🧩 编辑Mermaid图表</span>
                        <span style={{ 
                          fontSize: '11px', 
                          color: '#aaa',
                          fontStyle: 'italic',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          ⌨️ 单击图，按 ESC 键收起编辑器
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          onClick={() => setShowCodeEditor(false)}
                          style={{
                            padding: '2px 6px',
                            fontSize: '11px',
                            background: '#666',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}
                          title="收起编辑器"
                        >
                          ✕
                        </button>
                      </div>
                    </div>

                    {/* 图表预览区域 */}
                    <div style={{ padding: '12px', borderBottom: '1px solid #555' }}>
                      <EnhancedMermaid 
                        chart={props.code || 'graph TD\n  A[开始] --> B[结束]'} 
                        theme={mermaidTheme}
                        zoomEnabled={true}
                        showDebugInfo={false}
                      />
                    </div>
                    
                    {/* 代码编辑区域 */}
                    <div style={{ 
                      height: '250px',
                      borderTop: '1px solid #555'
                    }}>
                      <textarea
                        ref={textareaRef}
                        defaultValue={props.code}
                        onChange={(e) => {
                          // 使用防抖更新避免频繁渲染
                          debouncedUpdate(e.target.value);
                        }}
                        onKeyDown={(e) => e.nativeEvent.stopImmediatePropagation()}
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          padding: '12px',
                          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                          fontSize: '13px',
                          lineHeight: '1.5',
                          resize: 'none',
                          outline: 'none',
                          backgroundColor: '#1e1e1e',
                          color: '#d4d4d4'
                        }}
                        placeholder="在此输入Mermaid图表代码..."
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          }
        }
      ]
    }),
    codeMirrorPlugin({
      codeBlockLanguages: {
        txt: 'text',
        javascript: 'JavaScript',
        js: 'JavaScript',
        typescript: 'TypeScript',
        ts: 'TypeScript',
        jsx: 'JSX',
        tsx: 'TSX',
        css: 'CSS',
        html: 'HTML',
        json: 'JSON',
        markdown: 'Markdown',
        md: 'Markdown',
        python: 'Python',
        py: 'Python',
        java: 'Java',
        go: 'Go',
        rust: 'Rust',
        sql: 'SQL',
        yaml: 'YAML',
        yml: 'YAML',
        xml: 'XML',
        bash: 'Bash',
        shell: 'Shell',
        mermaid: 'Mermaid'
      }
    }),

    // 链接和图片
    linkPlugin(),
    linkDialogPlugin(),
    imagePlugin(),
    
    // 前言支持
    frontmatterPlugin(),

    // 差异对比（条件性启用）
    ...(diffMode ? [diffSourcePlugin({
      diffMarkdown: comparisonContent,
      viewMode: 'diff'
    })] : []),
    


    // 工具栏
    toolbarPlugin({
      toolbarContents: () => (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* 撤销重做 */}
          <UndoRedo />
          
          <div style={{ width: '1px', height: '24px', backgroundColor: '#555', margin: '0 4px' }} />
          
          {/* 格式化工具 */}
          <BoldItalicUnderlineToggles />
          <CodeToggle />
          
          <div style={{ width: '1px', height: '24px', backgroundColor: '#555', margin: '0 4px' }} />
          
          {/* 列表工具 */}
          <ListsToggle />
          
          <div style={{ width: '1px', height: '24px', backgroundColor: '#555', margin: '0 4px' }} />
          
          {/* 表格和分割线 */}
          <InsertTable />
          <InsertThematicBreak />
          
          <div style={{ width: '1px', height: '24px', backgroundColor: '#555', margin: '0 4px' }} />
          
          {/* 源码编辑按钮 */}
          {renderMode !== 'source' && (
            <button
              onClick={() => handleModeSwitch('source')}
              style={{
                fontSize: '11px',
                padding: '4px 8px',
                background: '#0078d4',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              title={t('editor_switch_source_title')}
            >
              {t('editor_switch_source')}
            </button>
          )}
          



          {/* 主题切换器 */}
          {enableThemeSwitch && (
            <>
              <div style={{ width: '1px', height: '24px', backgroundColor: '#555', margin: '0 4px' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#e6e6e6' }}>🎨</span>
                <select
                  value={currentTheme}
                  onChange={(e) => handleThemeChange(e.target.value)}
                  style={{
                    padding: '4px 6px',
                    fontSize: '12px',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    background: '#2d2d30',
                    color: '#e6e6e6',
                    cursor: 'pointer',
                    minWidth: '100px'
                  }}
                  title="切换编辑器主题"
                >
                  {THEME_OPTIONS.map(theme => (
                    <option key={theme.value} value={theme.value}>
                      {theme.icon} {theme.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* MDXEditor已内置撤销/重做功能，在工具栏中的UndoRedo组件 */}
        </div>
      )
    })
  ];

  // 只读模式下：拦截容器内所有链接点击，在新标签页打开
  useEffect(() => {
    if (isEditing) return; // 编辑模式由 linkDialogPlugin 处理，不干预
    const container = editorContainerRef.current;
    if (!container) return;

    const handleLinkClick = (e) => {
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;
      e.preventDefault();
      e.stopPropagation();
      window.open(anchor.href, '_blank', 'noopener,noreferrer');
    };

    container.addEventListener('click', handleLinkClick);
    return () => container.removeEventListener('click', handleLinkClick);
  }, [isEditing]);

  // 文件切换检测和内容同步
  const lastContentRef = useRef(content);
  const lastFileIdRef = useRef(fileId);
  
  useEffect(() => {
    // 使用fileId来精确检测文件切换，而不是内容变化
    const isFileSwitch = fileId !== lastFileIdRef.current;
    
    if (content !== lastContentRef.current || isFileSwitch) {
      // 立即保存当前修改到父组件（文件切换时不能丢失）
      if (window.mdxContentSyncTimeout) {
        clearTimeout(window.mdxContentSyncTimeout);
        // 立即执行待同步的内容保存
        if (onContentChange && currentContent !== lastContentRef.current) {
          onContentChange(fileId, currentContent, 'file-switch');
        }
      }
      
      // 只有真正的文件切换才重置状态
      if (isFileSwitch) {
        // 检测新内容是否包含数学公式
        const hasMath = containsMathFormula(content);
        const newMode = hasMath ? 'preview' : 'mdx';
        
        console.log('检测到文件切换:', { 
          from: lastFileIdRef.current, 
          to: fileId,
          hasMathFormula: hasMath,
          newRenderMode: newMode
        });
        
        lastFileIdRef.current = fileId;
        lastContentRef.current = content;
        setCurrentContent(content);
        setLastExternalContent(content);
        
        // 文件切换时：重置错误状态，根据内容选择合适的渲染模式
        setMdxError(null);
        setRenderMode(newMode); // 如果包含数学公式则使用 preview 模式
        setEditorKey(prev => prev + 1);

      } else {
        // 只是内容更新（例如Monaco Editor编辑或后端更新），不重置模式
        console.log('检测到内容更新（非文件切换）:', { 
          contentLength: content.length,
          keepCurrentMode: renderMode,
          isExpertDialog: fileId && fileId.includes('expert_')
        });
        lastContentRef.current = content;
        setLastExternalContent(content);
        setCurrentContent(content);
        
        // 关键修复：当内容更新但不是文件切换时，直接更新MDXEditor的内容
        if (mdxEditorRef.current?.setMarkdown && renderMode === 'mdx') {
          //console.log('直接更新MDXEditor内容');
          mdxEditorRef.current.setMarkdown(content);
        }
      }
    }
  }, [content, currentContent, onContentChange, fileId, renderMode]);

  // 插入主题样式
  useEffect(() => {
    insertThemeStyles();
    

    
    // 清理函数，在组件卸载时移除样式和定时器
    return () => {
      const existingStyle = document.getElementById('ybg-mdxeditor-theme');
      if (existingStyle) {
        existingStyle.remove();
      }

      
      // 清理防抖定时器，防止内存泄漏
      if (window.mdxContentSyncTimeout) {
        clearTimeout(window.mdxContentSyncTimeout);
      }
    };
  }, []);

  // 全局LLM命令API暴露
  useEffect(() => {
    if (enableLLMCommands && ref?.current) {
      window.ybgDocumentAPI = {
        // 基础内容操作
        setContent: (newContent, metadata = {}) => {
          ref.current?.setContent(newContent, metadata);
        },
        
        getContent: () => {
          return ref.current?.getContent() || '';
        },
        
        insertContent: (content, metadata = {}) => {
          ref.current?.insertContent(content, metadata);
        },
        
        // Mermaid专门操作
        replaceMermaidChart: (chartId, newChart, metadata = {}) => {
          const currentContent = ref.current?.getContent() || '';
          // 这里需要实现Mermaid图表的智能替换逻辑
          const updatedContent = replaceMermaidInContent(currentContent, chartId, newChart);
          ref.current?.setContent(updatedContent, {
            ...metadata,
            command: 'replaceMermaidChart',
            targetSection: chartId,
            description: `替换Mermaid图表: ${chartId}`
          });
        },
        
        // 历史管理
        getHistory: () => ref.current?.getHistory() || [],
        clearHistory: () => ref.current?.clearHistory(),
        
        // 差异对比
        showDiff: (compareContent) => ref.current?.showDiff(compareContent),
        hideDiff: () => ref.current?.hideDiff()
      };
    }

    // 清理函数
    return () => {
      if (window.ybgDocumentAPI && enableLLMCommands) {
        delete window.ybgDocumentAPI;
      }
    };
  }, [enableLLMCommands, ref]);

  return (
    <div 
      className={`enhanced-mdx-editor ${className}`}
      style={{
        border: '1px solid #444',
        borderRadius: '8px',
        overflow: 'visible',
        backgroundColor: '#252526',
        width: '100%',
        minHeight: '100%',  // 最小高度充满父容器，但允许内容扩展
        display: 'flex',
        flexDirection: 'column',
        ...style
      }}
    >

      
      {/* 顶部工具栏 - 模式切换按钮 */}
      <div style={{
        padding: '8px 12px',
        borderBottom: '1px solid #444',
        backgroundColor: '#2d2d30',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {/* 左侧：模式显示 */}
        <span style={{ fontSize: '12px', color: '#e6e6e6' }}>
          {renderMode === 'mdx' && (isEditing ? t('editor_richtext_mode') : t('editor_richtext_readonly'))}
          {renderMode === 'preview' && (
            <>
              {t('editor_preview_mode')}
              {containsMathFormula(currentContent) && (
                <span style={{ color: '#4fc3f7', marginLeft: '8px', fontSize: '11px' }}>
                  {t('editor_preview_math_hint')}
                </span>
              )}
            </>
          )}
          {renderMode === 'source' && t('editor_source_mode')}
        </span>

        {/* 右侧：模式切换按钮 */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          
          {/* 切换到富文本模式按钮 */}
          {renderMode !== 'mdx' && (
            <button
              onClick={() => handleModeSwitch('mdx')}
              style={{
                fontSize: '11px',
                padding: '4px 8px',
                background: '#0d7377',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              title={t('editor_switch_richtext_title')}
            >
              {t('editor_switch_richtext')}
            </button>
          )}
          
          {/* 只在只读模式中显示源码编辑按钮 */}
          {renderMode === 'preview' && (
            <button
              onClick={() => handleModeSwitch('source')}
              style={{
                fontSize: '11px',
                padding: '4px 8px',
                background: '#0078d4',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginLeft: '6px'
              }}
              title={t('editor_switch_source_title')}
            >
              {t('editor_switch_source')}
            </button>
          )}
          
          {renderMode !== 'preview' && mdxError && (
            <button
              onClick={() => handleModeSwitch('preview')}
              style={{
                fontSize: '11px',
                padding: '4px 8px',
                background: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              title={t('editor_switch_preview_title')}
            >
              {t('editor_switch_preview')}
            </button>
          )}
        </div>
      </div>

      {/* 渲染模式切换 - 内容区域 */}
      <div style={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {renderMode === 'mdx' && (
          <>

            
            {/* MDX编辑器 */}
            <div 
              ref={editorContainerRef}
              style={{ 
                position: 'relative', 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column'
              }}
            >
              <MDXEditor
                key={editorKey} // 文件切换时强制重新渲染
                ref={(editor) => {
                  mdxEditorRef.current = editor;
                  if (editor) {
                    mdxEditorReadyRef.current = true;
                    //console.log('MDXEditor实例已就绪');
                  }
                }}
                className={currentThemeConfig.className}
                markdown={currentContent || ''}
                onChange={handleContentChange}
                plugins={plugins}
                readOnly={!isEditing}
                contentEditableClassName="mdx-editor-content"
                onError={handleMDXError}
                {...otherProps}
              />
            </div>
          </>
        )}

        {renderMode === 'preview' && (
          <div style={{ 
            padding: '8px',
            backgroundColor: '#1e1e1e',
            color: '#d4d4d4',
            minHeight: '100%'
          }}>


            {/* 内容区域 - 自然扩展，使用外部滚动 */}
            <div>
              {renderFinalMarkdown(fileId || 'preview.final', currentContent, {
                // 传递空的选项，因为预览模式不需要行选择等交互功能
                selectedFileLines: {},
                handleFileLineSelection: () => {}
              })}
            </div>
          </div>
        )}

        {renderMode === 'source' && (
          <div style={{ flex: 1, height: '100%', position: 'relative' }}>
            <SourceCodeEditor
            content={currentContent}              // B变量：当前编辑内容
            originalContent={originalContent}     // A变量：原始内容
            fileId={fileId}
            currentPhase={currentPhase}           // 🔑 传递当前阶段
            documentPhase={documentPhase}         // 🔑 传递文档阶段
            ws={ws}                               // 🔑 传递WebSocket连接
            addLocalMessage={addLocalMessage}     // 🔑 传递本地消息添加函数
            onChange={(newContent) => {
              setCurrentContent(newContent);
              setSourceContent(newContent);
              if (onContentChange) {
                onContentChange(fileId, newContent, 'source-edit');
              }
            }}
            onSave={(operation) => {
              // 保存操作，不切换模式，保持用户当前的编辑模式
              console.log('SourceCodeEditor 保存完成:', operation);
              
              // 🔑 新增：处理重置修订基准操作
              if (operation === 'reset-revision-baseline' && onContentChange) {
                console.log('执行重置修订基准：将B变量赋值给A变量');
                // 触发重置修订基准，将当前内容(B变量)赋值给原始内容(A变量)
                onContentChange(fileId, currentContent, 'reset-revision-baseline');
              }
            }}
            theme={currentTheme}
            onModeSwitch={handleModeSwitch}
          />
          </div>
        )}
      </div>
      
      {/* 状态指示器 */}
      <div style={{
        padding: '4px 8px',
        background: '#2d2d30',
        borderTop: '1px solid #444',
        fontSize: '11px',
        color: '#e6e6e6',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>
          字符数: {currentContent.length}
          {diffMode && ' | 🔍 差异对比模式'}
          {renderMode === 'source' && diffResults && (
            <span style={{ color: '#8b5cf6', marginLeft: '8px' }}>
              | 📝 修订: +{diffResults.insertCount}字符 -{diffResults.deleteCount}字符
            </span>
          )}
          {mdxError && (
            <span style={{ color: '#e6a23c', marginLeft: '8px' }} title={`渲染异常自动降级: ${mdxError.message}`}>
              | ⚠️ 兼容渲染模式
            </span>
          )}
        </span>
        
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {/* 主题信息显示 */}
          <span style={{ fontSize: '10px', color: '#ccc' }}>
            主题: {currentThemeConfig.icon} {currentThemeConfig.label}
          </span>
        </div>
      </div>
    </div>
  );
});

// Mermaid内容替换辅助函数
const replaceMermaidInContent = (content, chartId, newChart) => {
  // 这是一个简化的替换逻辑，实际可能需要更复杂的解析
  const lines = content.split('\n');
  let inMermaidBlock = false;
  let mermaidBlockIndex = 0;
  let startIndex = -1;
  let endIndex = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.startsWith('```mermaid')) {
      if (chartId === `mermaid-${mermaidBlockIndex}` || !chartId) {
        inMermaidBlock = true;
        startIndex = i;
      }
      mermaidBlockIndex++;
    } else if (line === '```' && inMermaidBlock) {
      endIndex = i;
      break;
    }
  }

  if (startIndex !== -1 && endIndex !== -1) {
    const beforeBlock = lines.slice(0, startIndex + 1);
    const afterBlock = lines.slice(endIndex);
    const newMermaidLines = newChart.split('\n');
    
    return [...beforeBlock, ...newMermaidLines, ...afterBlock].join('\n');
  }

  return content;
};

// 生成修订显示的HTML - 适配深色背景
const generateRevisionHTML = (diffs) => {
  return diffs.map(([operation, text]) => {
    const escapedText = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br/>');
    
    switch (operation) {
      case 1: // 插入 - 明亮的蓝色，适合深色背景
        return `<span style="color: #60a5fa; background-color: rgba(96, 165, 250, 0.2); font-weight: 500;">${escapedText}</span>`;
      case -1: // 删除 - 明亮的红色，适合深色背景
        return `<span style="color: #f87171; text-decoration: line-through; background-color: rgba(248, 113, 113, 0.2);">${escapedText}</span>`;
      case 0: // 不变 - 正常的浅色文字
      default:
        return `<span style="color: #d4d4d4;">${escapedText}</span>`;
    }
  }).join('');
};

// 源码编辑器组件 - 使用 Monaco Editor DiffEditor
// 移除processMDXContent函数，让MDXEditor自然处理格式，在首次进入时同步A和B变量

const SourceCodeEditor = ({ content, originalContent, onChange, onSave, theme, fileId, onModeSwitch, currentPhase, documentPhase, ws, addLocalMessage }) => {
  // 直接使用originalContent，不再进行标准化处理
  const standardizedOriginal = originalContent;
  
  // 不处理content(B变量)，因为它已经被MDXEditor处理过
  const [editContent, setEditContent] = useState(content);
  const editorRef = useRef(null);
  const disposablesRef = useRef([]); // 存储需要清理的监听器
  
  // AI编辑相关状态
  const [selectedText, setSelectedText] = useState('');
  const [currentSelection, setCurrentSelection] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editInstruction, setEditInstruction] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastRequestId, setLastRequestId] = useState('');

  // 基于内容变化更新编辑内容
  useEffect(() => {
    console.log('SourceCodeEditor 内容变化:', { 
      fileId,
      contentLength: content.length,
      originalLength: originalContent.length,
      时间戳: new Date().toLocaleTimeString(),
      isExpertDialog: fileId && fileId.includes('expert_')
    });
    
    // 直接使用content(B变量)，不再应用标准化处理
    // B变量已经被MDXEditor处理过，具有一致的格式
    setEditContent(content);
  }, [content, originalContent, fileId]);

  // 文件切换时清理和重建监听器
  useEffect(() => {
    // 清理现有监听器
    disposablesRef.current.forEach(disposable => {
      if (disposable && disposable.dispose) {
        disposable.dispose();
      }
    });
    disposablesRef.current = [];
  }, [fileId]);

  // 组件卸载时的清理
  useEffect(() => {
    return () => {
      // 清理所有监听器
      disposablesRef.current.forEach(disposable => {
        if (disposable && disposable.dispose) {
          disposable.dispose();
        }
      });
      disposablesRef.current = [];
      
      // 清理编辑器引用
      if (editorRef.current) {
        try {
          editorRef.current.dispose();
        } catch (error) {
          console.warn('Monaco Editor 清理错误:', error);
        }
        editorRef.current = null;
      }
    };
  }, []);

  const handleSave = () => {
    if (onChange) {
      onChange(editContent);
    }
    onSave();
  };

  const handleCancel = () => {
    // 重置到原始内容（A变量）
    setEditContent(standardizedOriginal);
    if (onChange) {
      onChange(originalContent); // 返回原始内容给父组件
    }
  };

  // AI编辑相关函数
  const handleAIEditClick = () => {
    if (!editorRef.current) return;
    
    const editor = editorRef.current.getModifiedEditor();
    const selection = editor.getSelection();
    
    if (selection && !selection.isEmpty()) {
      const selectedContent = editor.getModel().getValueInRange(selection);
      setSelectedText(selectedContent);
      setCurrentSelection(selection);
      setIsEditMode(true);
      setEditInstruction('');
    } else {
      alert('请先选中要编辑的文本');
    }
  };

  const handleEditSubmit = () => {
    if (!editInstruction.trim()) {
      alert('请输入编辑指令');
      return;
    }

    const requestId = Date.now().toString();
    setLastRequestId(requestId);
    setIsProcessing(true);

    // 调用全局的requestContentEdit函数
    if (window.requestContentEdit) {
      window.requestContentEdit(
        selectedText,
        editInstruction.trim(),
        fileId,
        currentSelection,
        requestId
      );
    }
  };

  const handleEditCancel = () => {
    setIsEditMode(false);
    setSelectedText('');
    setCurrentSelection(null);
    setEditInstruction('');
  };

  // 🔑 获取Monaco Editor修订信息
  const getMonacoRevisions = (diffEditor) => {
    try {
      const lineChanges = diffEditor.getLineChanges();
      const originalModel = diffEditor.getOriginalEditor().getModel();
      const modifiedModel = diffEditor.getModifiedEditor().getModel();
      
      if (!lineChanges || lineChanges.length === 0) {
        return "无修订";
      }

      const revisions = lineChanges.map((change, index) => {
        const originalContent = originalModel.getValueInRange({
          startLineNumber: change.originalStartLineNumber,
          startColumn: 1,
          endLineNumber: change.originalEndLineNumber,
          endColumn: originalModel.getLineMaxColumn(change.originalEndLineNumber)
        }).trim();

        const modifiedContent = modifiedModel.getValueInRange({
          startLineNumber: change.modifiedStartLineNumber,
          startColumn: 1,
          endLineNumber: change.modifiedEndLineNumber,
          endColumn: modifiedModel.getLineMaxColumn(change.modifiedEndLineNumber)
        }).trim();

        return `修订${index + 1}: 行${change.originalStartLineNumber}-${change.originalEndLineNumber} | 原始: "${originalContent}" | 修改后: "${modifiedContent}"`;
      }).join("\n");
      
      // 🔑 长度限制：如果超过50000字符，从尾部截断
      if (revisions.length > 50000) {
        const truncated = revisions.substring(0, 50000);
        return truncated + "\n...(修订详情过长，已截断)";
      }
      
      return revisions;

    } catch (error) {
      console.error("📄 [获取修订] Monaco差异解析失败:", error);
      return "修订信息获取失败";
    }
  };

  // 🔑 重置修订基准的通用逻辑
  const resetRevisionBaseline = () => {
    if (onChange) {
      // 先通知父组件更新当前内容
      onChange(editContent);
      console.log("📄 [重置基准] 触发重置修订基准，将B变量赋值给A变量");
      
      // 通过onSave回调触发重置基准操作
      if (onSave) {
        // 延迟执行以确保内容更新完成
        setTimeout(() => {
          onSave('reset-revision-baseline');
        }, 100);
      }
    }
  };

  // 🔑 新增：文件保存逻辑
  const handleFileSave = () => {
    console.log("📄 [文件保存] 保存按钮点击:", {
      fileId,
      当前阶段: currentPhase,
      文档阶段: documentPhase,
      内容长度: editContent.length,
      WebSocket状态: ws?.readyState,
      时间戳: new Date().toISOString()
    });

    // 检查文档阶段与当前阶段是否匹配
    if (!documentPhase || !currentPhase) {
      console.warn("📄 [文件保存] 阶段信息缺失:", { documentPhase, currentPhase });
      alert("阶段信息缺失，无法保存");
      return;
    }

    if (documentPhase !== currentPhase) {
      console.warn("📄 [文件保存] 文档阶段与当前阶段不匹配:", { 
        文档阶段: documentPhase, 
        当前阶段: currentPhase 
      });
      
      // 🔑 新增：询问用户是否要跨阶段保存
      const shouldCrossPhaseSave = window.confirm(
        `文档阶段(${documentPhase})与当前阶段(${currentPhase})不匹配。\n\n` +
        `点击"确定"进行跨阶段保存\n点击"取消"放弃保存`
      );
      
      if (!shouldCrossPhaseSave) {
        return;
      }
      
      // 🔑 跨阶段保存：使用新的消息类型
      handleCrossPhaseSave();
      return;
    }

    // 检查WebSocket连接
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.error("📄 [文件保存] WebSocket连接不可用");
      alert("网络连接异常，无法保存");
      return;
    }

    try {
      // 获取修订信息
      let revisionsInfo = "无差异编辑器可用";
      if (editorRef.current) {
        revisionsInfo = getMonacoRevisions(editorRef.current);
      }

      // 🔑 检查是否有修订内容
      if (revisionsInfo === "无修订") {
        alert("无修订，无需保存！");
        console.log("📄 [文件保存] 检测到无修订内容，跳过保存操作");
        return;
      }

      // 发送文件更新消息
      const updateMessage = {
        type: "humaninput",
        input: `^^FILE_UPDATE:${editContent}`,
        session_id: localStorage.getItem('sessionId'),
        user_id: JSON.parse(localStorage.getItem("userInfo") || "{}")?.username
      };

      ws.send(JSON.stringify(updateMessage));
      console.log("📄 [文件保存] 文件更新消息已发送");

      // 发送包含修订信息的操作通知消息
      const notifyMessage = {
        type: "humaninput",
        input: `^^NOTIFY:我修改了文档 ${fileId || 'unknown'}，修订详情:\n ${revisionsInfo}`,
        session_id: localStorage.getItem('sessionId'),
        user_id: JSON.parse(localStorage.getItem("userInfo") || "{}")?.username
      };

      //控制台打印修订详情
      console.log("📄 [文件保存] 修订详情:", revisionsInfo);

      // 🔑 新增：添加本地聊天消息显示修订详情
      if (addLocalMessage) {
        const localMessageContent = `📝 **我修改了文档，修订详情如下：**\n\n**文档**: ${fileId || 'unknown'}\n**阶段**: ${documentPhase || '未知'}\n\n**修订内容**:\n${revisionsInfo}`;
        addLocalMessage(localMessageContent, "HumanInput");
      }

      ws.send(JSON.stringify(notifyMessage));
      console.log("📄 [文件保存] 操作通知消息已发送:", {
        修订信息: revisionsInfo
      });

      // 🔑 保存成功后重置修订基准
      resetRevisionBaseline();

      // 保存成功提示
      alert("文件已保存并同步到后端");

    } catch (error) {
      console.error("📄 [文件保存] 保存失败:", error);
      alert("保存失败: " + error.message);
    }
  };

  // 🔑 新增：跨阶段保存处理函数
  const handleCrossPhaseSave = () => {
    try {
      // 获取修订信息（复用现有逻辑）
      let revisionsInfo = "无差异编辑器可用";
      if (editorRef.current) {
        revisionsInfo = getMonacoRevisions(editorRef.current);
      }

      // 🔑 检查是否有修订内容
      if (revisionsInfo === "无修订") {
        alert("无修订，无需保存！");
        console.log("📄 [文件保存] 检测到无修订内容，跳过保存操作");
        return;
      }
      
      // 🔑 复用AI编辑的逻辑：通过全局函数获取文件元数据
      let fileMeta = { filename: "", role: "", phase: "", roleContext: "", taskFilename: "" };
      if (window.getFileMeta) {
        fileMeta = window.getFileMeta(fileId);
        console.log("📄 [跨阶段保存] 获取文件元数据:", fileMeta);
      }
      
      // 发送跨阶段保存消息
      const crossPhaseSaveMessage = {
        type: "cross_phase_document_save",
        session_id: localStorage.getItem('sessionId'),
        user_id: JSON.parse(localStorage.getItem("userInfo") || "{}")?.username,
        file_id: fileId,
        content: editContent,
        document_phase: documentPhase,
        current_phase: currentPhase,
        // 🔑 按照training_final_docs的存储格式发送数据
        filename: fileMeta.filename,           // 对应training_final_docs.filename
        phase: fileMeta.phase,                 // 对应training_final_docs.phase  
        role: fileMeta.role,                   // 对应training_final_docs.role
        task_filename: fileMeta.taskFilename,  // 对应training_final_docs.taskFilename
        revisions_info: revisionsInfo,
        timestamp: new Date().toISOString()
      };
      
      ws.send(JSON.stringify(crossPhaseSaveMessage));
      console.log("📄 [跨阶段保存] 消息已发送:", {
        fileId,
        documentPhase,
        currentPhase,
        contentLength: editContent.length
      });
      
      // 添加本地聊天消息
      if (addLocalMessage) {
        const localMessageContent = `🔄 **跨阶段文档保存**\n\n**文档**: ${fileId}\n**文档阶段**: ${documentPhase}\n**当前阶段**: ${currentPhase}\n\n**修订内容**:\n${revisionsInfo}`;
        addLocalMessage(localMessageContent, "HumanInput");
      }
      
      // 🔑 跨阶段保存成功后重置修订基准
      resetRevisionBaseline();

      alert("跨阶段文档保存请求已发送");
      
    } catch (error) {
      console.error("📄 [跨阶段保存] 失败:", error);
      alert("跨阶段保存失败: " + error.message);
    }
  };

  const applyEditResult = (editedText, requestId) => {
    if (requestId !== lastRequestId) return; // 防止旧请求覆盖新请求
    
    if (editorRef.current && currentSelection) {
      const editor = editorRef.current.getModifiedEditor();
      
      // 使用Monaco Editor的executeEdits API应用编辑结果
      editor.executeEdits('ai-edit', [{
        range: currentSelection,
        text: editedText
      }]);
      
      // 重置编辑状态
      setIsEditMode(false);
      setSelectedText('');
      setCurrentSelection(null);
      setEditInstruction('');
      setIsProcessing(false);
    }
  };

  // 监听全局编辑成功事件
  useEffect(() => {
    const handleEditSuccess = (event) => {
      const { editedText, requestId } = event.detail;
      applyEditResult(editedText, requestId);
    };

    const handleEditError = (event) => {
      const { message } = event.detail;
      alert('编辑失败: ' + message);
      setIsProcessing(false);
    };

    window.addEventListener('contentEditSuccess', handleEditSuccess);
    window.addEventListener('contentEditError', handleEditError);

    return () => {
      window.removeEventListener('contentEditSuccess', handleEditSuccess);
      window.removeEventListener('contentEditError', handleEditError);
    };
  }, [lastRequestId, currentSelection]);

  // Monaco Editor 挂载时的回调
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    
    // 清理之前的监听器
    disposablesRef.current.forEach(disposable => {
      if (disposable && disposable.dispose) {
        disposable.dispose();
      }
    });
    disposablesRef.current = [];
    
    // 获取修改后的模型（右侧编辑器）
    const modifiedModel = editor.getModifiedEditor().getModel();
    
    if (modifiedModel) {
      // 监听内容变化，并保存disposable
      const disposable = modifiedModel.onDidChangeContent(() => {
        const newContent = modifiedModel.getValue();
        // 只更新编辑内容，不更新基准内容
        setEditContent(newContent);
        // 实时通知父组件
        if (onChange) {
          onChange(newContent);
        }
      });
      
      // 保存监听器以便后续清理
      disposablesRef.current.push(disposable);
    }
  };

  return (
    <div style={{ 
      backgroundColor: '#1e1e1e',
      height: '100%',
      position: 'relative'
    }}>

      {/* AI编辑工具栏 */}
      <div style={{
        padding: '8px 12px',
        backgroundColor: '#2d2d30',
        borderBottom: '1px solid #444',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '12px',
        color: '#e6e6e6'
      }}>
        <button
          onClick={handleAIEditClick}
          disabled={isProcessing}
          style={{
            padding: '4px 8px',
            backgroundColor: '#0e639c',
            color: 'white',
            border: 'none',
            borderRadius: '3px',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            fontSize: '11px',
            opacity: isProcessing ? 0.6 : 1
          }}
        >
          🤖 AI编辑
        </button>
        
        <button
          onClick={handleFileSave}
          disabled={isProcessing}
          style={{
            padding: '4px 8px',
            backgroundColor: '#0e7490',
            color: 'white',
            border: 'none',
            borderRadius: '3px',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            fontSize: '11px',
            opacity: isProcessing ? 0.6 : 1
          }}
        >
          💾 保存
        </button>
        
        {selectedText && (
          <span style={{ color: '#ffcc02' }}>
            已选中: {selectedText.length > 50 ? selectedText.substring(0, 50) + '...' : selectedText}
          </span>
        )}
        
        {isProcessing && (
          <span style={{ color: '#4ec9b0' }}>正在处理中...</span>
        )}
      </div>

      {/* 编辑指令输入框 */}
      {isEditMode && (
        <div style={{
          padding: '12px',
          backgroundColor: '#3c3c3c',
          borderBottom: '1px solid #444',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ color: '#e6e6e6', fontSize: '12px', minWidth: '60px' }}>
            编辑指令:
          </span>
          <input
            type="text"
            value={editInstruction}
            onChange={(e) => setEditInstruction(e.target.value)}
            placeholder="例如: 将这段文字改得更专业、添加技术细节、优化表达..."
            style={{
              flex: 1,
              padding: '4px 8px',
              backgroundColor: '#1e1e1e',
              border: '1px solid #555',
              borderRadius: '3px',
              color: '#e6e6e6',
              fontSize: '12px'
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleEditSubmit();
              }
            }}
            autoFocus
          />
          <button
            onClick={handleEditSubmit}
            disabled={isProcessing || !editInstruction.trim()}
            style={{
              padding: '4px 12px',
              backgroundColor: '#0e639c',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: (isProcessing || !editInstruction.trim()) ? 'not-allowed' : 'pointer',
              fontSize: '11px',
              opacity: (isProcessing || !editInstruction.trim()) ? 0.6 : 1
            }}
          >
            {isProcessing ? '处理中...' : '确认'}
          </button>
          <button
            onClick={handleEditCancel}
            disabled={isProcessing}
            style={{
              padding: '4px 12px',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              fontSize: '11px',
              opacity: isProcessing ? 0.6 : 1
            }}
          >
            取消
          </button>
        </div>
      )}
      
      {/* Monaco DiffEditor */}
      <DiffEditor
        height="calc(100vh - 180px)" // 调整高度，为AI编辑工具栏留出空间
        language="markdown"
        theme="vs-dark"
        original={standardizedOriginal}    // A变量：原始内容（已经在首次进入时同步为MDXEditor处理后的格式）
        modified={editContent}        // B变量：当前编辑内容
        onMount={handleEditorDidMount}
        options={{
          renderSideBySide: false, // inline 差异显示
          readOnly: false,
          minimap: { enabled: false },
          lineNumbers: 'on',
          wordWrap: 'on',
          scrollBeyondLastLine: false,
          // 保留滚动条功能
          scrollbar: {
            vertical: 'visible',
            horizontal: 'auto',
            useShadows: true,
            verticalHasArrows: false,
            horizontalHasArrows: false
          },
          automaticLayout: true,
          fontSize: 14,
          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
          renderIndicators: true,
          renderMarginRevertIcon: false,
          diffWordWrap: 'on',
          
          // 关键设置：完全忽略空白字符差异
          ignoreTrimWhitespace: true,
          
          // 使用最智能的比较算法
          diffAlgorithm: 'advanced',
          
          // 关键设置：忽略前导和尾随空白
          compareWithLCS: true,
          
          // 关键设置：忽略空行变化
          ignoreEmptyLines: true,
          
          // 关键设置：忽略缩进变化
          ignoreIndentation: true,
          
          // 设置最小比较单位为单词而非字符
          wordWrap: 'on',
          
          // 设置最大比较深度，减少过度比较
          maxComputationTime: 5000,
          
          // 增加比较的宽容度
          computeMinChanges: true,
          
          enableSplitViewResizing: false,
          // 确保内联差异渲染
          renderOverviewRuler: false,
          folding: false,
          // 确保修改编辑器可编辑
          originalEditable: false,
          diffCodeLens: true,
          // 增强颜色对比
          colorDecorators: true
        }}
      />
      

    </div>
  );
};



EnhancedMDXEditor.displayName = 'EnhancedMDXEditor';

// 导出增强型MDX编辑器组件
// 已移除processMDXContent函数导出
export default EnhancedMDXEditor;